import axios from 'axios';

const API_BASE_URL = 'https://files.bihebari.com/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 30000
});

// Request interceptor to add token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only redirect to login on 401 authentication errors
    if (error.response?.status === 401) {
      // Don't redirect if we're already on login page
      if (!window.location.pathname.includes('/login')) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('admin');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Add these to your API service file

export const coverPhotoAPI = {
  // Get cover photos with filters
  getCoverPhotos: (params) => {
    const { page = 1, limit = 20, status = 'pending' } = params;
    return api.get(`users/admin/cover-photos`, {
      params: { page, limit, status }
    });
  },

  // Approve a cover photo
  approveCoverPhoto: (userId) => {
    return api.put(`/users/admin/users/${userId}/cover-photo/approve`);
  },

  // Reject a cover photo
  rejectCoverPhoto: (userId, data) => {
    return api.put(`users/admin/users/${userId}/cover-photo/reject`, data);
  },

  // Delete a cover photo
  deleteCoverPhoto: (userId) => {
    return api.delete(`users/admin/users/${userId}/cover-photo`);
  }
};

// Dashboard APIs
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getCharts: () => api.get('/dashboard/charts'),
  getActivities: (limit = 10) => api.get(`/dashboard/activities?limit=${limit}`)
};

// User APIs
export const userAPI = {
  getUsers: (params) => api.get('/users', { params }),
  getUserDetails: (id) => api.get(`/users/${id}`),
  blockUser: (id, block) => api.put(`/users/${id}/block`, { block }),
  verifyUser: (id) => api.put(`/users/${id}/verify`),
  deleteUser: (id) => api.delete(`/users/${id}`)
};

// Verification APIs
export const verificationAPI = {
  getPendingDocuments: () => api.get('/verification/pending-documents'),
  approveDocument: (id, notes) => api.put(`/verification/documents/${id}/approve`, { admin_notes: notes }),
  rejectDocument: (id, notes) => api.put(`/verification/documents/${id}/reject`, { admin_notes: notes }),
  getPendingPhotos: () => api.get('/verification/pending-photos'),
  approvePhoto: (type, id) => api.put(`/verification/photos/${type}/${id}/approve`),
  rejectPhoto: (type, id) => api.delete(`/verification/photos/${type}/${id}`)
};

// Payment APIs
export const paymentAPI = { 
  getTransactions: (params) => api.get('/payments/transactions', { params }),
  getSubscriptions: () => api.get('/payments/subscriptions'),
  getPackages: () => api.get('/payments/packages'),
  updatePackage: (id, data) => api.put(`/payments/packages/${id}`, data),
  addPackage: (data) => api.post('/payments/packages', data),
  deletePackage: (id) => api.delete(`/payments/packages/${id}`)
};

// Report APIs
export const reportAPI = {
  getUserReports: (status) => api.get('/reports/user-reports', { params: { status } }),
  updateReport: (id, status, action) => api.put(`/reports/user-reports/${id}`, { status, action }),
  generateAnalytics: (data) => api.post('/reports/analytics', data)
};

// Settings APIs
// Add these to your existing settingsAPI
export const settingsAPI = {
  getMasterData: () => api.get('/settings/master-data'),
  updateMasterData: (table, id, data) => api.put(`/settings/master-data/${table}/${id}`, data),
  addMasterData: (table, data) => api.post(`/settings/master-data/${table}`, data),
  deleteMasterData: (table, id) => api.delete(`/settings/master-data/${table}/${id}`),
  // New cascading endpoints
  getStates: (countryId) => api.get(`/settings/states/${countryId}`),
  getCities: (stateId) => api.get(`/settings/cities/${stateId}`),
  getCommunitiesByReligion: (religionId) => api.get(`/settings/communities/${religionId}`)
};

// src/services/api.js - Add these to your existing file

// Add to your existing API service file
export const bannerAPI = {
  // Get all banners with pagination
  getBanners: (params) => {
    const { page = 1, limit = 10, search, status } = params;
    return api.get('/admin/banners', {
      params: { page, limit, search, status }
    });
  },

  // Get single banner by ID
  getBanner: (id) => api.get(`/admin/banners/${id}`),

  // Create new banner
  createBanner: (data) => api.post('/admin/banners', data),

  // Update banner
  updateBanner: (id, data) => api.put(`/admin/banners/${id}`, data),

  // Delete banner
  deleteBanner: (id) => api.delete(`/admin/banners/${id}`),

  // Upload banner image
  uploadBannerImage: (file) => {
    const formData = new FormData();
    formData.append('banner_image', file);
    return api.post('/admin/banners/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  // Toggle banner status
  toggleStatus: (id, currentStatus) => {
    const newStatus = currentStatus === 1 ? 0 : 1;
    return api.put(`/admin/banners/${id}`, { status: newStatus });
  }
};

// You can also add it to your existing exports

export default api;